// Invoice PDF Generator
// Uses jsPDF to create professional purple-branded invoice PDFs
import { jsPDF } from 'jspdf';

const PURPLE = [47, 0, 78]; // #2f004e — matches Satellite Gardens logo
const DARK = [26, 26, 26];
const GRAY = [102, 102, 102];
const LIGHT_GRAY = [153, 153, 153];
const LINE_COLOR = [224, 224, 224];

// Load logo as base64 from public folder
let logoBase64 = null;
let logoLoaded = false;

async function loadLogo() {
  if (logoLoaded) return logoBase64;
  try {
    const response = await fetch('/logo-purple.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        logoBase64 = reader.result;
        logoLoaded = true;
        resolve(logoBase64);
      };
      reader.onerror = () => {
        logoLoaded = true;
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    logoLoaded = true;
    return null;
  }
}

function fmtMoney(n) {
  return '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export async function generateInvoicePDF(invoice) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth(); // 612
  const margin = 50;
  const rightEdge = pw - margin;
  let y = margin;

  // Load logo
  const logo = await loadLogo();

  // ── HEADER ──
  // Logo on left — 2x size
  if (logo) {
    try {
      doc.addImage(logo, 'PNG', margin, y - 15, 120, 84);
    } catch (e) {
      console.warn('Logo failed to load in PDF:', e);
    }
  }

  // INVOICE title on right — vertically centered with logo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...PURPLE);
  doc.text('INVOICE', rightEdge, y + 16, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...GRAY);
  doc.text(`#${invoice.invoiceNumber}`, rightEdge, y + 34, { align: 'right' });

  // Move y past the logo (logo is 84pt tall from y-15)
  y += 80;

  // Purple divider — fully below logo
  doc.setDrawColor(...PURPLE);
  doc.setLineWidth(2);
  doc.line(margin, y, rightEdge, y);
  y += 24;

  // ── BILL TO / INVOICE DETAILS ──
  // Left: Bill To
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('BILL TO', margin, y);

  y += 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...DARK);
  doc.text(invoice.clientName || '', margin, y);

  if (invoice.clientAddress) {
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text(invoice.clientAddress, margin, y);
  }

  if (invoice.clientEmail) {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text(invoice.clientEmail, margin, y);
  }

  // Right: Invoice details — three dates stacked vertically
  const detailsX = rightEdge - 160;
  const valX = detailsX + 90;
  let dy = y - (invoice.clientAddress ? 44 : 28);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('INVOICE DATE', detailsX, dy);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(fmtDateShort(invoice.createdAt?.split('T')[0] || invoice.periodEnd), valX, dy);

  dy += 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('PERIOD START', detailsX, dy);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(fmtDateShort(invoice.periodStart), valX, dy);

  dy += 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('PERIOD END', detailsX, dy);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(fmtDateShort(invoice.periodEnd), valX, dy);

  dy += 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('STATUS', detailsX, dy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PURPLE);
  doc.text((invoice.status || 'draft').toUpperCase(), valX, dy);

  y += 40;

  // ── LINE ITEMS TABLE ──
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(1);
  doc.line(margin, y, rightEdge, y);
  y += 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('DESCRIPTION', margin, y);
  doc.text('DETAILS', rightEdge - 180, y, { align: 'right' });
  doc.text('AMOUNT', rightEdge, y, { align: 'right' });

  y += 10;
  doc.line(margin, y, rightEdge, y);
  y += 6;

  function addLineItem(description, details, amount) {
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(description, margin, y);

    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text(details, rightEdge - 180, y, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(fmtMoney(amount), rightEdge, y, { align: 'right' });

    y += 10;
    doc.setDrawColor(...LINE_COLOR);
    doc.setLineWidth(0.5);
    doc.line(margin, y, rightEdge, y);
  }

  if (invoice.laborTotal > 0) {
    addLineItem('Labor', `${invoice.laborHours}h × ${fmtMoney(invoice.laborRate)}`, invoice.laborTotal);
  }

  if (invoice.materialsTotal > 0) {
    addLineItem('Materials', `${fmtMoney(invoice.materialsSubtotal)} + 10% markup`, invoice.materialsTotal);
  }

  if (invoice.subcontractorsTotal > 0) {
    addLineItem('Subcontractors', `${fmtMoney(invoice.subcontractorsSubtotal)} + 10% markup`, invoice.subcontractorsTotal);
  }

  // ── TOTAL ──
  y += 30;
  const totalBoxWidth = 220;
  const totalBoxHeight = 50;
  const totalBoxX = rightEdge - totalBoxWidth;

  doc.setFillColor(...PURPLE);
  doc.roundedRect(totalBoxX, y, totalBoxWidth, totalBoxHeight, 6, 6, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL DUE', totalBoxX + 16, y + 22);

  doc.setFontSize(22);
  doc.text(fmtMoney(invoice.grandTotal), totalBoxX + totalBoxWidth - 16, y + 24, { align: 'right' });

  y += totalBoxHeight + 40;

  // ── PAYMENT TERMS ──
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.5);
  doc.line(margin, y, rightEdge, y);
  y += 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('PAYMENT TERMS', margin, y);

  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text('Payment due within 30 days of invoice date.', margin, y);
  y += 14;
  doc.text('Please make checks payable to Satellite Gardens.', margin, y);

  // ── FOOTER — Quote ──
  const footerY = doc.internal.pageSize.getHeight() - 50;
  doc.setDrawColor(...PURPLE);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 8, rightEdge, footerY - 8);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...PURPLE);
  doc.text('"The ornament of a house is the friends who frequent it."', pw / 2, footerY + 8, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...LIGHT_GRAY);
  doc.text('— Ralph Waldo Emerson', pw / 2, footerY + 22, { align: 'center' });

  // Save
  const fileName = `SatelliteGardens_Invoice_${invoice.invoiceNumber}.pdf`;
  doc.save(fileName);
  return fileName;
}
